import React, { useEffect, useState } from 'react';
import View from './view';
import {
  post as uploadEventReport,
  useGetFestReport,
  invalidate,
  usePostGroupReport,
} from 'actions/cuemath-fest-reports';
import { useGetAllLumaEvents } from 'actions/luma-events';
import { useDispatch, useSelector } from 'react-redux';
import { store } from 'redux-rest-reducer';
import moment from 'moment';

const defaultState = {
  date: moment().format('YYYY-MM-DD'),
  eventName: '',
  eventId: '',
  eventDataFile: '',
  dateTs: +new Date(moment().format('YYYY-MM-DD')),
  selectedEventDetails: {},
  certificateEventName: '',
};

const FestReport = () => {
  const dispatch = useDispatch();
  const [reportUpload] = useState(store.post());
  const [events, setEvents] = useState([]);
  const [getEventsState, setGetEventsState] = useState({
    isLoading: false,
    error: null,
    isLoaded: false,
  });
  const [formData, setFormData] = useState(defaultState);

  const { id: reportUploadPostId } = reportUpload || {};
  const {
    eventName,
    eventDataFile,
    dateTs,
    eventId,
    selectedEventDetails,
    certificateEventName,
  } = formData || {};
  const eventStartTime = new Date(dateTs).toISOString();
  const eventEndTime = new Date(dateTs + 24 * 60 * 60 * 1000 - 1000).toISOString();

  const eventDropdownOptions = events?.map(event => {
    const eventDetails = event?.event || {};
    const updatedEvent = { ...event };
    updatedEvent.value = eventDetails.name;
    updatedEvent.label = eventDetails.name;
    updatedEvent.id = eventDetails.app_id;

    return updatedEvent;
  });

  const previousReportsQuery = { event_id: eventId };
  const previouslyUploadedReportsData = useSelector(state => state?.festReports);
  const { resolved, current } = store.post(
    previouslyUploadedReportsData,
    reportUploadPostId,
  );
  const { isCreating } = current || {};
  const { isCreated } = resolved || {};

  const {
    get: getFestReport,
    data: previouslyUploadedReports,
    isStale,
    isProcessing,
  } = useGetFestReport(eventId, previousReportsQuery);

  const {
    post: sendCommsPost,
    isProcessing: commsSendingInProgress,
  } = usePostGroupReport({
    onComplete: () => {
      invalidate(eventId, previousReportsQuery);
    },
  });

  const {
    getAll: getAllLumaEvents,
    data: lumaEvents,
    isProcessed,
  } = useGetAllLumaEvents();

  useEffect(() => {
    if (isProcessed && lumaEvents) {
      setEvents(lumaEvents);
      setGetEventsState({
        isLoading: false,
        error: null,
        isLoaded: true,
      });
    }
  }, [isProcessed, lumaEvents]);

  useEffect(() => {
    if (dateTs) {
      setGetEventsState({
        isLoading: true,
      });
      if (events?.length > 0) {
        setEvents([]);
      }
      if (eventName || eventDataFile || dateTs) {
        setFormData({
          ...formData,
          eventName: '',
          eventDataFile: '',
          dateTs: 0,
          selectedEventDetails: {},
          eventId: '',
        });
      }
      getAllLumaEvents({
        before: `${eventEndTime}`,
        after: `${eventStartTime}`,
        visibility: 'public',
      });
    }
  }, [dateTs]);

  const handleGetFestReport = () => {
    getFestReport(eventId, previousReportsQuery);
  };

  useEffect(() => {
    if (eventId) {
      handleGetFestReport();
    }
  }, [eventId]);

  useEffect(() => {
    if (isStale && eventId) {
      handleGetFestReport();
    }
  }, [isStale]);

  useEffect(() => {
    if (isCreated) {
      invalidate(eventId, previousReportsQuery);
    }
  }, [eventId, isCreated]);

  const handleFormChange = (fieldId, data) => {
    let updatedFormData = { ...formData, [fieldId]: data };

    if (fieldId === 'eventName') {
      const selectedEventDetails = events?.find(event => {
        const eventDetails = event?.event || {};

        return data === eventDetails.name;
      });

      updatedFormData = {
        ...updatedFormData,
        eventId: selectedEventDetails?.api_id,
        selectedEventDetails,
        eventDataFile: '',
        certificateEventName: data,
      };
    }

    if (fieldId === 'date') {
      updatedFormData.dateTs = +new Date(data);
    }

    setFormData(updatedFormData);
  };

  const handleUpload = () => {
    const formData = new FormData();
    formData.append('file', eventDataFile);
    formData.append('event_id', eventId);
    formData.append('visible_event_name', certificateEventName);
    formData.append('meta_data', JSON.stringify(selectedEventDetails));

    dispatch(uploadEventReport(reportUploadPostId, formData));
  };

  const sendCommsToParticipants = groupReportId => {
    if (commsSendingInProgress) return;
    sendCommsPost({ comms_medium: 'email' }, { groupReportId });
  };

  return (
    <View
      formData={formData}
      handleUpload={handleUpload}
      getEventsState={getEventsState}
      handleFormChange={handleFormChange}
      handleGetFestReport={handleGetFestReport}
      eventDropdownOptions={eventDropdownOptions}
      uploadingFileInProgress={isCreating}
      getReportDetailsInProgress={isProcessing}
      previouslyUploadedReports={previouslyUploadedReports}
      sendCommsToParticipants={sendCommsToParticipants}
    />
  );
};

export default FestReport;
